import {
  PosCashOutAmountLine,
  PosCashOutCategoryTotal,
  PosCashOutOccupiedTable,
  PosCashOutOrderPreview,
  PosCashOutResume,
  PosCashOutRow,
  PosCashOutTipEmployee,
  PosCashOutTipShift,
} from '../../models/pos-traslado.models';
import { coerceMoneyFromApi } from '@shared/utils/money';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickString(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

function pickMoney(raw: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (raw[key] != null && raw[key] !== '') {
      return coerceMoneyFromApi(raw[key]) ?? 0;
    }
  }
  return 0;
}

function pickNumber(raw: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const n = Number(value);
      if (Number.isFinite(n)) {
        return n;
      }
    }
  }
  return 0;
}

function pickBool(raw: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return false;
}

function pickStringList(raw: Record<string, unknown>, ...keys: string[]): readonly string[] {
  for (const key of keys) {
    const value = raw[key];
    if (Array.isArray(value)) {
      return value
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map((item) => item.trim());
    }
  }
  return [];
}

export function mapAmountLines(raw: unknown): readonly PosCashOutAmountLine[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const lines: PosCashOutAmountLine[] = [];
  for (const item of raw) {
    const data = asRecord(item);
    if (!data) {
      continue;
    }
    const name = pickString(data, 'name') || '';
    if (!name) {
      continue;
    }
    lines.push({
      accountId: pickString(data, 'accountId', 'account_id'),
      name,
      net: pickMoney(data, 'net'),
      gross: pickMoney(data, 'gross'),
      feeProfile: pickString(data, 'feeProfile', 'fee_profile'),
    });
  }
  return lines;
}

export function mapCategoryTotals(raw: unknown): readonly PosCashOutCategoryTotal[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const totals: PosCashOutCategoryTotal[] = [];
  for (const item of raw) {
    const data = asRecord(item);
    if (!data) {
      continue;
    }
    const name = pickString(data, 'name');
    if (!name) {
      continue;
    }
    totals.push({ name, total: pickMoney(data, 'total') });
  }
  return totals;
}

function mapOccupiedTable(raw: unknown): PosCashOutOccupiedTable | null {
  const data = asRecord(raw);
  if (!data) {
    return null;
  }
  const id = pickString(data, 'id');
  const name = pickString(data, 'name');
  if (!id || !name) {
    return null;
  }
  return { id, name };
}

function mapTipEmployee(raw: unknown): PosCashOutTipEmployee | null {
  const data = asRecord(raw);
  if (!data) {
    return null;
  }
  const id = pickString(data, 'id');
  if (!id) {
    return null;
  }
  return {
    id,
    name: pickString(data, 'name') || '',
    roleName: pickString(data, 'roleName', 'role_name'),
    percentage: pickNumber(data, 'percentage'),
    estimatedTips: pickMoney(data, 'estimatedTips', 'estimated_tips'),
  };
}

function mapTipShift(raw: unknown): PosCashOutTipShift | null {
  const data = asRecord(raw);
  if (!data) {
    return null;
  }
  const id = pickString(data, 'id');
  if (!id) {
    return null;
  }
  const employeesRaw = data['employees'];
  const employees = Array.isArray(employeesRaw)
    ? employeesRaw.map(mapTipEmployee).filter((e): e is PosCashOutTipEmployee => e != null)
    : [];
  return {
    id,
    businessDate: pickString(data, 'businessDate', 'business_date'),
    totalTips: pickMoney(data, 'totalTips', 'total_tips'),
    employees,
  };
}

function mapOrderPreview(raw: unknown): PosCashOutOrderPreview | null {
  const data = asRecord(raw);
  if (!data) {
    return null;
  }
  const id = pickString(data, 'id');
  if (!id) {
    return null;
  }
  return {
    id,
    dateCreated: pickString(data, 'dateCreated', 'date_created'),
    discountTotal: pickMoney(data, 'discountTotal', 'discount_total'),
    status: pickString(data, 'status') || '',
    total: pickMoney(data, 'total'),
    paymentMethodTitle: pickString(data, 'paymentMethodTitle', 'payment_method_title') || '',
    products: pickString(data, 'products') || '',
    shippingTotal: pickMoney(data, 'shippingTotal', 'shipping_total'),
    shippingAddress: pickString(data, 'shippingAddress', 'shipping_address') || '',
  };
}

export function mapPosCashOutResume(rawInput: unknown): PosCashOutResume {
  const raw = asRecord(rawInput) ?? {};
  const categoryTotals = mapCategoryTotals(raw['categoryTotals'] ?? raw['category_totals']);
  const occupied = Array.isArray(raw['occupiedTables'] ?? raw['occupied_tables'])
    ? ((raw['occupiedTables'] ?? raw['occupied_tables']) as unknown[])
        .map(mapOccupiedTable)
        .filter((t): t is PosCashOutOccupiedTable => t != null)
    : [];
  const tipShifts = Array.isArray(raw['openTipShifts'] ?? raw['open_tip_shifts'])
    ? ((raw['openTipShifts'] ?? raw['open_tip_shifts']) as unknown[])
        .map(mapTipShift)
        .filter((s): s is PosCashOutTipShift => s != null)
    : [];
  const orders = Array.isArray(raw['orders'])
    ? raw['orders'].map(mapOrderPreview).filter((o): o is PosCashOutOrderPreview => o != null)
    : [];

  const totalGeneral = pickMoney(raw, 'totalGeneral', 'total_general');

  return {
    startDatetime: pickString(raw, 'startDatetime', 'start_datetime') || '',
    endDatetime: pickString(raw, 'endDatetime', 'end_datetime') || '',
    cashOutDate: pickString(raw, 'cashOutDate', 'cash_out_date') || '',
    totalGeneral,
    tipTotal: pickMoney(raw, 'tipTotal', 'tip_total'),
    amountLines: mapAmountLines(raw['amountLines'] ?? raw['amount_lines']),
    categoryTotals,
    ordersCount: pickNumber(raw, 'ordersCount', 'orders_count') || orders.length,
    orders,
    occupiedTables: occupied,
    openOrdersCount: pickNumber(raw, 'openOrdersCount', 'open_orders_count'),
    pendingManagerCartsCount: pickNumber(
      raw,
      'pendingManagerCartsCount',
      'pending_manager_carts_count',
    ),
    operationBlockingError:
      pickString(raw, 'operationBlockingError', 'operation_blocking_error') || null,
    openTipShifts: tipShifts,
    alreadyExists: pickBool(raw, 'alreadyExists', 'already_exists'),
    periodOverlap: pickBool(raw, 'periodOverlap', 'period_overlap'),
    unmatchedAccountNames: pickStringList(raw, 'unmatchedAccountNames', 'unmatched_account_names'),
    missingTipsPayableAccount: pickBool(
      raw,
      'missingTipsPayableAccount',
      'missing_tips_payable_account',
    ),
    canComplete: pickBool(raw, 'canComplete', 'can_complete'),
  };
}

export function mapPosCashOutRow(raw: Record<string, unknown>): PosCashOutRow {
  const statusRaw = (pickString(raw, 'status') || 'active').toLowerCase();
  const status = statusRaw === 'voided' ? 'voided' : 'active';
  const voided = pickBool(raw, 'voided') || status === 'voided';
  return {
    id: pickString(raw, 'id') || '',
    number: pickNumber(raw, 'number'),
    cashOutDate: pickString(raw, 'cashOutDate', 'cash_out_date'),
    locationId: pickString(raw, 'locationId', 'location_id') || '',
    userId: pickString(raw, 'userId', 'user_id') || '',
    userName: pickString(raw, 'userName', 'user_name'),
    totalVenta: pickMoney(raw, 'totalVenta', 'total_venta'),
    totalVentaBruta: pickMoney(raw, 'totalVentaBruta', 'total_venta_bruta'),
    tipsPayableTotal: pickMoney(raw, 'tipsPayableTotal', 'tips_payable_total'),
    createdAt: pickString(raw, 'createdAt', 'created_at'),
    amountLines: mapAmountLines(raw['amountLines'] ?? raw['amount_lines']),
    status,
    voided,
    voidReason: pickString(raw, 'voidReason', 'void_reason'),
    voidedAt: pickString(raw, 'voidedAt', 'voided_at'),
  };
}

/** Filas de montos desde amountLines (dinámico por cuenta). */
export function posCashOutAmountRows(
  options: {
    onlyPositive?: boolean;
    amountLines?: readonly PosCashOutAmountLine[];
  } = {}
): ReadonlyArray<{ key: string; labelKey: string; label?: string; value: number }> {
  const lines = options.amountLines ?? [];
  const rows = lines.map((line) => ({
    key: line.accountId || line.name,
    labelKey: '',
    label: line.name,
    value: line.net,
  }));
  return options.onlyPositive ? rows.filter((row) => row.value > 0) : rows;
}
